import React, { useState, useMemo, useEffect } from 'react';

/* ============================================================================
   Yool · YoolTable — tabla canónica del Design System
   Portado desde apps-design-system/project/components/YoolTable/YoolTable.jsx
   API: columns · rows · rowKey · search · filters · initialSort · toolbar ·
        empty · dense · variant · footer
   Helpers re-exportados: StatusPill · ProviderChip · RowActions · IconButton ·
                          MonoText · StackedCell · IconCell
   ============================================================================ */

const T = {
  border: '#E8E8E8', borderStrong: '#E0E0E0',
  text: '#1A1A1A', sub: '#6B7176', muted: '#8D8D8D',
  head: '#9499A0', surface: '#FFFFFF', zebra: '#FAFAFA',
  hover: '#F7F8F9', cta: '#007D6E',
};

const TBL_FALLBACK_ICONS = {
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm10 2-4.35-4.35',
  chevronDown: 'M6 9l6 6 6-6',
  edit: 'M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5Z',
  trash: 'M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8ZM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6Z',
  plus: 'M12 5v14M5 12h14',
  refresh: 'M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15',
};

function TIcon({ name, size = 16 }) {
  // Priorizamos el fallback local: cubre exactamente los íconos que la tabla usa
  // (search, edit, trash, eye, plus, refresh, chevronDown) y garantiza que se
  // rendericen. El set del design system (window.YIcon) NO cubre todos los
  // nombres —p.ej. "edit" devuelve null— así que solo lo usamos para los que no
  // tenemos en el fallback.
  const d = TBL_FALLBACK_ICONS[name];
  if (!d) {
    const YI = (typeof window !== 'undefined') && window.YIcon;
    return YI ? React.createElement(YI, { name, size }) : null;
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={d} />
    </svg>
  );
}

/* ── Status pill — colores semánticos del BrandBook (nunca teal) ──────────── */
const PILL = {
  activo:     { c: '#0A5C36', bg: '#DCF3E6', t: 'Activo' },
  active:     { c: '#0A5C36', bg: '#DCF3E6', t: 'Activo' },
  pagado:     { c: '#0A5C36', bg: '#DCF3E6', t: 'Pagado' },
  pagada:     { c: '#0A5C36', bg: '#DCF3E6', t: 'Pagada' },
  activa:     { c: '#0A5C36', bg: '#DCF3E6', t: 'Activa' },
  completado: { c: '#0A5C36', bg: '#DCF3E6', t: 'Completado' },
  borrador:   { c: '#54585A', bg: '#ECECEC', t: 'Borrador' },
  inactivo:   { c: '#54585A', bg: '#ECECEC', t: 'Inactivo' },
  inactive:   { c: '#54585A', bg: '#ECECEC', t: 'Inactivo' },
  cancelado:  { c: '#54585A', bg: '#ECECEC', t: 'Cancelado' },
  cancelled:  { c: '#54585A', bg: '#ECECEC', t: 'Cancelado' },
  pendiente:  { c: '#854D0E', bg: '#FBEFC8', t: 'Pendiente' },
  pending:    { c: '#854D0E', bg: '#FBEFC8', t: 'Pendiente' },
  procesando: { c: '#0369A1', bg: '#DBEEFB', t: 'Procesando' },
  processing: { c: '#0369A1', bg: '#DBEEFB', t: 'Procesando' },
  pausado:    { c: '#854D0E', bg: '#FBEFC8', t: 'Pausado' },
  paused:     { c: '#854D0E', bg: '#FBEFC8', t: 'Pausado' },
  pausada:    { c: '#854D0E', bg: '#FBEFC8', t: 'Pausada' },
  error:      { c: '#C0004A', bg: '#FCDCE6', t: 'Error' },
  fallido:    { c: '#C0004A', bg: '#FCDCE6', t: 'Fallido' },
  failed:     { c: '#C0004A', bg: '#FCDCE6', t: 'Fallido' },
  expirado:   { c: '#C0004A', bg: '#FCDCE6', t: 'Expirado' },
  expired:    { c: '#C0004A', bg: '#FCDCE6', t: 'Expirado' },
  nuevo:      { c: '#7E22CE', bg: '#F2E4FE', t: 'Nuevo' },
  new:        { c: '#7E22CE', bg: '#F2E4FE', t: 'Nuevo' },
  completed:  { c: '#0A5C36', bg: '#DCF3E6', t: 'Completado' },
  paid:       { c: '#0A5C36', bg: '#DCF3E6', t: 'Pagado' },
  authorized: { c: '#0369A1', bg: '#DBEEFB', t: 'Autorizado' },
  refunded:   { c: '#7E22CE', bg: '#F2E4FE', t: 'Reembolsado' },
  archivado:  { c: '#54585A', bg: '#ECECEC', t: 'Archivado' },
};

export function StatusPill({ estado, children, tone = 'solid' }) {
  const k = String(estado || '').toLowerCase();
  const p = PILL[k] || { c: T.sub, bg: '#ECECEC', t: children || estado };
  if (tone === 'ring') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 11.5, fontWeight: 600, padding: '2px 9px', borderRadius: 7, background: p.bg, color: p.c, boxShadow: 'inset 0 0 0 1px ' + p.c + '33', whiteSpace: 'nowrap', letterSpacing: '-0.005em' }}>
        {children || p.t}
      </span>
    );
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11.5, fontWeight: 600, padding: '3px 10px 3px 8px', borderRadius: 999, background: p.bg, color: p.c, whiteSpace: 'nowrap', letterSpacing: '-0.005em' }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: p.c, flexShrink: 0 }} />
      {children || p.t}
    </span>
  );
}

/* ── Provider chip — medio de pago con color de marca ─────────────────────── */
const PROVIDERS = {
  mercadopago:   { c: '#00B1EA', t: 'MercadoPago' },
  flow:          { c: '#1A73E8', t: 'Flow' },
  virtualpos:    { c: '#6366F1', t: 'VirtualPOS' },
  webpay:        { c: '#FF6B35', t: 'Webpay' },
  webpay_plus:   { c: '#FF6B35', t: 'Webpay Plus' },
  transbank:     { c: '#E30613', t: 'Transbank' },
  vd:            { c: '#E30613', t: 'Débito' },
  vn:            { c: '#E30613', t: 'Crédito' },
  vc:            { c: '#E30613', t: 'Crédito' },
  si:            { c: '#E30613', t: 'Sin Interés' },
  s2:            { c: '#E30613', t: '2 cuotas' },
  nc:            { c: '#E30613', t: 'Sin cuotas' },
  transfer:      { c: '#6B7176', t: 'Transferencia' },
  bank_transfer: { c: '#6B7176', t: 'Trans. bancaria' },
  credit_card:   { c: '#6B7176', t: 'Tarjeta crédito' },
  debit_card:    { c: '#6B7176', t: 'Tarjeta débito' },
};

export function ProviderChip({ id, label, color }) {
  const p = PROVIDERS[String(id || '').toLowerCase()] || { c: color || T.sub, t: label || id };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, color: T.text, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.c, flexShrink: 0 }} />{p.t}
    </span>
  );
}

/* ── Cell helpers ─────────────────────────────────────────────────────────── */
export function MonoText({ children }) {
  return (
    <span style={{ fontFamily: "'SF Mono','Fira Mono',ui-monospace,monospace", fontSize: 12.5, color: T.sub }}>
      {children}
    </span>
  );
}

export function StackedCell({ primary, secondary, mono }) {
  return (
    <div style={{ lineHeight: 1.3 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: T.text, whiteSpace: 'nowrap' }}>{primary}</div>
      {secondary != null && (
        <div style={{ fontSize: 11, color: T.muted, fontFamily: mono ? "'SF Mono','Fira Mono',ui-monospace,monospace" : 'inherit' }}>
          {secondary}
        </div>
      )}
    </div>
  );
}

export function IconCell({ icon, primary, secondary, accent = T.cta }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
      <span style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, display: 'grid', placeItems: 'center', background: accent + '16', color: accent }}>
        <TIcon name={icon} size={16} />
      </span>
      <div style={{ lineHeight: 1.3, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{primary}</div>
        {secondary != null && <div style={{ fontSize: 11.5, color: T.muted }}>{secondary}</div>}
      </div>
    </div>
  );
}

export function IconButton({ icon, title, onClick, danger, children }) {
  const [h, setH] = useState(false);
  return (
    <button title={title} onClick={onClick}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        width: 32, height: 32, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        lineHeight: 0, borderRadius: 9, cursor: 'pointer',
        border: '1px solid ' + (h ? (danger ? '#F4B8C9' : T.borderStrong) : 'transparent'),
        background: h ? (danger ? '#FCEDF1' : T.hover) : 'transparent',
        color: danger ? '#C0004A' : (h ? T.text : T.sub), transition: 'all .14s ease',
      }}>
      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {children ?? <TIcon name={icon} size={16} />}
      </span>
    </button>
  );
}

// `center`: para columnas de una sola acción, donde alinear a la derecha deja
// el botón descolgado del encabezado. Por defecto sigue a la derecha.
export function RowActions({ children, center = false }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: center ? 'center' : 'flex-end' }}>
      {children}
    </div>
  );
}

function SortGlyph({ state }) {
  const up = state === 'asc', down = state === 'desc';
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" style={{ flexShrink: 0 }} aria-hidden="true">
      <path d="M5 1 L8.2 5 L1.8 5 Z" fill={up ? T.cta : '#C7CBD0'} />
      <path d="M5 13 L8.2 9 L1.8 9 Z" fill={down ? T.cta : '#C7CBD0'} />
    </svg>
  );
}

function normFilterOpts(options) {
  return (options || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
}

function PagerButton({ children, onClick, disabled, title }) {
  const [h, setH] = useState(false);
  return (
    <button
      type="button" title={title} onClick={onClick} disabled={disabled}
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        height: 30, minWidth: 30, padding: '0 10px', borderRadius: 8,
        border: '1px solid ' + T.borderStrong,
        background: disabled ? '#FAFAFA' : (h ? T.hover : '#fff'),
        color: disabled ? '#C7CBD0' : T.text,
        fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
        cursor: disabled ? 'default' : 'pointer', transition: 'all .14s ease',
      }}>
      {children}
    </button>
  );
}

/**
 * YoolTable — tabla canónica de Yool.
 * columns: [{ key, label, sortable?, sortValue?, render?, align?, numeric?, width?, wrap? }]
 * pageSize: opcional. Si se pasa (> 0) la tabla pagina en cliente DESPUÉS de
 *   buscar/filtrar/ordenar, así la búsqueda sigue cubriendo todas las filas.
 *   Sin la prop, la tabla renderiza todo como siempre.
 *
 * Modo servidor (opt-in): pasando `totalRows` la tabla deja de buscar, filtrar y
 *   paginar por su cuenta — renderiza `rows` tal cual y delega en quien la usa
 *   vía `onSearchChange` / `onFilterChange` / `onPageChange`. Sirve cuando el
 *   conjunto es demasiado grande para traerlo entero al navegador.
 */
export function YoolTable({
  columns = [], rows = [], rowKey = 'id',
  search, filters = [], initialSort,
  toolbar, empty, dense = false, variant = 'card', footer,
  pageSize,
  totalRows, page: pageProp, onPageChange,
  searchValue, onSearchChange,
  filterValues, onFilterChange,
  busy = false,
}) {
  const serverMode = typeof totalRows === 'number';
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(initialSort || null);
  const [fvals, setFvals] = useState({});

  // En modo servidor los valores los manda el contenedor; si no, son estado interno.
  const qActual = serverMode ? (searchValue ?? '') : q;
  const setQActual = serverMode ? (onSearchChange || (() => {})) : setQ;
  const fvalsActuales = serverMode ? (filterValues || {}) : fvals;

  const toggleSort = (key) => {
    setSort((s) => {
      if (!s || s.key !== key) return { key, dir: 'asc' };
      if (s.dir === 'asc') return { key, dir: 'desc' };
      return null;
    });
  };

  const visible = useMemo(() => {
    // En modo servidor las filas ya vienen buscadas, filtradas y paginadas.
    if (serverMode) return rows;
    let out = rows.slice();
    if (search && q.trim()) {
      const needle = q.trim().toLowerCase();
      const keys = search.keys || columns.map((c) => c.key);
      out = out.filter((r) => keys.some((k) => String(r[k] ?? '').toLowerCase().includes(needle)));
    }
    filters.forEach((f) => {
      const v = fvals[f.key];
      if (v) out = out.filter((r) => String(r[f.key]) === String(v));
    });
    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      const get = (r) => (col && col.sortValue ? col.sortValue(r) : r[sort.key]);
      out.sort((a, b) => {
        const av = get(a), bv = get(b);
        let cmp;
        if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
        else cmp = String(av ?? '').localeCompare(String(bv ?? ''), 'es', { numeric: true });
        return sort.dir === 'asc' ? cmp : -cmp;
      });
    }
    return out;
  }, [rows, q, sort, fvals, columns, filters, search, serverMode]);

  /* ── Paginación (en cliente vía `pageSize`, o del servidor vía `totalRows`) ── */
  const paginated = Number(pageSize) > 0;
  const perPage = paginated ? Number(pageSize) : 0;
  const [pageInterna, setPageInterna] = useState(1);
  const totalItems = serverMode ? totalRows : visible.length;
  const totalPages = paginated ? Math.max(1, Math.ceil(totalItems / perPage)) : 1;
  const safePage = serverMode
    ? Math.max(1, Number(pageProp) || 1)
    : Math.min(pageInterna, totalPages);
  const irAPagina = serverMode ? (onPageChange || (() => {})) : setPageInterna;

  // Al buscar, filtrar u ordenar cambia el conjunto: volver a la primera página
  // para no quedar en una página que ya no existe. En modo servidor lo maneja
  // el contenedor, que es quien dispara la consulta.
  useEffect(() => {
    if (!serverMode) setPageInterna(1);
  }, [q, fvals, sort, rows, serverMode]);

  const pageRows = paginated && !serverMode
    ? visible.slice((safePage - 1) * perPage, safePage * perPage)
    : visible;
  const fromRow = totalItems === 0 ? 0 : (safePage - 1) * perPage + 1;
  const toRow = serverMode
    ? Math.min((safePage - 1) * perPage + rows.length, totalItems)
    : Math.min(safePage * perPage, totalItems);

  const hasToolbar = search || filters.length > 0 || toolbar;
  const cellPadV = dense ? 9 : 13;
  const plain = variant === 'plain';
  const showFooter = footer != null ? footer : !plain;
  const selBase = {
    height: 38, padding: '0 34px 0 12px', borderRadius: 10,
    border: '1.5px solid ' + T.borderStrong, background: '#fff', color: T.text,
    fontSize: 13, fontWeight: 500, cursor: 'pointer', appearance: 'none',
    outline: 'none', fontFamily: 'inherit',
  };

  return (
    <div style={plain
      ? { background: 'transparent' }
      : { background: T.surface, border: '1px solid ' + T.borderStrong, borderRadius: 16, overflow: 'hidden' }}>

      {hasToolbar && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 16, borderBottom: '1px solid ' + T.border, flexWrap: 'wrap' }}>
          {search && (
            <div style={{ position: 'relative', flex: '1 1 220px', minWidth: 180, maxWidth: 340 }}>
              <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: T.muted, pointerEvents: 'none' }}>
                <TIcon name="search" size={16} />
              </span>
              <input
                value={qActual}
                onChange={(e) => setQActual(e.target.value)}
                placeholder={search.placeholder || 'Buscar…'}
                style={{ width: '100%', height: 38, padding: '0 12px 0 36px', borderRadius: 10, border: '1.5px solid ' + T.borderStrong, fontSize: 13, color: T.text, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }}
                onFocus={(e) => (e.target.style.borderColor = T.cta)}
                onBlur={(e) => (e.target.style.borderColor = T.borderStrong)}
              />
            </div>
          )}
          {filters.map((f) => (
            <div key={f.key} style={{ position: 'relative' }}>
              <select
                value={fvalsActuales[f.key] || ''}
                disabled={f.disabled}
                title={f.title}
                onChange={(e) => (serverMode
                  ? (onFilterChange || (() => {}))(f.key, e.target.value)
                  : setFvals((s) => ({ ...s, [f.key]: e.target.value })))}
                style={{ ...selBase, ...(f.disabled ? { background: '#FAFAFA', color: T.muted, cursor: 'not-allowed' } : null) }}
                onFocus={(e) => (e.target.style.borderColor = T.cta)}
                onBlur={(e) => (e.target.style.borderColor = T.borderStrong)}>
                <option value="">{f.label}</option>
                {normFilterOpts(f.options).map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              <span style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', color: T.muted, pointerEvents: 'none' }}>
                <TIcon name="chevronDown" size={15} />
              </span>
            </div>
          ))}
          {toolbar && (
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
              {toolbar}
            </div>
          )}
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {columns.map((c) => {
                const sortState = sort && sort.key === c.key ? sort.dir : null;
                const align = c.align || 'left';
                return (
                  <th key={c.key} style={{ background: T.zebra, borderBottom: '1px solid ' + T.borderStrong, padding: '10px 16px', textAlign: align, width: c.width, whiteSpace: 'nowrap', position: 'sticky', top: 0 }}>
                    <button
                      disabled={!c.sortable}
                      onClick={() => c.sortable && toggleSort(c.key)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        flexDirection: align === 'right' ? 'row-reverse' : 'row',
                        background: 'none', border: 0, padding: 0, margin: 0,
                        cursor: c.sortable ? 'pointer' : 'default',
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
                        textTransform: 'uppercase', color: sortState ? T.text : T.head,
                        fontFamily: 'inherit',
                      }}>
                      {c.label}
                      {c.sortable && <SortGlyph state={sortState} />}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r, ri) => (
              <tr
                key={r[rowKey] ?? ri}
                style={{ borderBottom: '1px solid ' + T.border, transition: 'background .12s ease' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = T.hover)}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                {columns.map((c) => (
                  <td key={c.key} style={{ padding: `${cellPadV}px 16px`, textAlign: c.align || 'left', color: T.text, verticalAlign: 'middle', whiteSpace: c.wrap ? 'normal' : 'nowrap', fontVariantNumeric: c.numeric ? 'tabular-nums' : 'normal' }}>
                    {c.render ? c.render(r) : (r[c.key] ?? <span style={{ color: T.muted }}>—</span>)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pageRows.length === 0 && (
        <div style={{ padding: '56px 24px', textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: 14, background: '#F2F2F2', color: T.muted, margin: '0 auto 14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <TIcon name={(empty && empty.icon) || 'search'} size={22} />
          </div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: T.text }}>
            {(empty && empty.title) || 'Sin resultados'}
          </p>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: T.muted, maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
            {(empty && empty.text) || 'Ajusta la búsqueda o los filtros para ver más.'}
          </p>
        </div>
      )}

      {showFooter && totalItems > 0 && (
        <div style={{ padding: '11px 16px', borderTop: '1px solid ' + T.border, fontSize: 12, color: T.muted, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span>
            {paginated
              ? `${fromRow}–${toRow} de ${totalItems} ${totalItems === 1 ? 'resultado' : 'resultados'}`
              : `${totalItems} ${totalItems === 1 ? 'resultado' : 'resultados'}`}
            {!serverMode && rows.length !== visible.length ? ` · de ${rows.length}` : ''}
          </span>
          {paginated && totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <PagerButton title="Primera página" onClick={() => irAPagina(1)} disabled={busy || safePage === 1}>«</PagerButton>
              <PagerButton title="Página anterior" onClick={() => irAPagina(safePage - 1)} disabled={busy || safePage === 1}>Anterior</PagerButton>
              <span style={{ padding: '0 6px', fontWeight: 600, color: T.sub, whiteSpace: 'nowrap' }}>
                Página {safePage} de {totalPages}
              </span>
              <PagerButton title="Página siguiente" onClick={() => irAPagina(safePage + 1)} disabled={busy || safePage === totalPages}>Siguiente</PagerButton>
              <PagerButton title="Última página" onClick={() => irAPagina(totalPages)} disabled={busy || safePage === totalPages}>»</PagerButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
